import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ListOrdered, Lock, Minus, Plus, Trash2 } from "lucide-react";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { useGeocodePreview, GeocodePreview, geocodeLabel } from "./GeocodePreview";

const AddStopInline = ({ prefix, onAdd }) => {
  const [input, setInput] = useState("");
  const geo = useGeocodePreview(input);
  const label = geocodeLabel(geo);
  const add = () => {
    if (!label) return;
    onAdd(label);
    setInput("");
  };
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Input data-testid={`${prefix}-add-stop-input`} value={input} onChange={(e) => setInput(e.target.value)}
          placeholder='Tussenstop toevoegen (bv. "Kassel, Duitsland")' className="rounded-xl flex-1 h-9 text-sm"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
        <Button data-testid={`${prefix}-add-stop-button`} type="button" variant="secondary" size="sm" onClick={add}
          disabled={geo.checking || !geo.result} className="rounded-xl shrink-0 h-9">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <GeocodePreview value={input} geo={geo} testId={`${prefix}-add-stop-geo`} />
    </div>
  );
};

const StopRow = ({ s, idx, total, prefix, onMove, onNights, onDelete }) => (
  <div data-testid={`${prefix}-stop-row-${idx}`} className="bg-secondary/60 rounded-xl px-3 py-2.5 flex items-center gap-2">
    <div className="flex flex-col gap-0.5 shrink-0">
      <button type="button" data-testid={`${prefix}-stop-up-${idx}`} disabled={idx === 0} onClick={() => onMove(idx, -1)}
        className="p-1 rounded-md hover:bg-background disabled:opacity-25 transition-[background-color] duration-150">
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" data-testid={`${prefix}-stop-down-${idx}`} disabled={idx === total - 1} onClick={() => onMove(idx, 1)}
        className="p-1 rounded-md hover:bg-background disabled:opacity-25 transition-[background-color] duration-150">
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold truncate">{s.location}</p>
      {s.name && (
        <p className="text-xs text-emerald-700 truncate flex items-center gap-1">
          <Lock className="w-3 h-3 shrink-0" /> {s.name}
        </p>
      )}
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <button type="button" data-testid={`${prefix}-stop-nights-minus-${idx}`} disabled={s.nights <= 1} onClick={() => onNights(idx, -1)}
        className="w-6 h-6 rounded-full bg-background hover:bg-primary/15 flex items-center justify-center disabled:opacity-25 transition-[background-color] duration-150">
        <Minus className="w-3 h-3" />
      </button>
      <span data-testid={`${prefix}-stop-nights-${idx}`} className="font-mono font-bold text-sm w-14 text-center">{s.nights} n.</span>
      <button type="button" data-testid={`${prefix}-stop-nights-plus-${idx}`} disabled={s.nights >= 30} onClick={() => onNights(idx, 1)}
        className="w-6 h-6 rounded-full bg-background hover:bg-primary/15 flex items-center justify-center disabled:opacity-25 transition-[background-color] duration-150">
        <Plus className="w-3 h-3" />
      </button>
    </div>
    <button type="button" data-testid={`${prefix}-stop-delete-${idx}`} onClick={() => onDelete(idx)}
      className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 shrink-0 transition-[background-color,color] duration-150">
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  </div>
);

export const RouteStopsDialog = ({ trip, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [heen, setHeen] = useState([]);
  const [terug, setTerug] = useState([]);
  const [busy, setBusy] = useState(false);
  const norm = (l) => (l || []).map((s) => ({ location: s.location, nights: Math.max(1, parseInt(s.nights) || 1), name: s.name || null }));
  const move = (set) => (idx, dir) => set((l) => { const c = [...l]; const [x] = c.splice(idx, 1); c.splice(idx + dir, 0, x); return c; });
  const nights = (set) => (idx, d) => set((l) => l.map((s, i) => i === idx ? { ...s, nights: Math.min(30, Math.max(1, s.nights + d)) } : s));
  const del = (set) => (idx) => set((l) => l.filter((_, i) => i !== idx));
  const totalNights = [...heen, ...terug].reduce((a, s) => a + s.nights, 0);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/trips/${trip.id}/stops`, { planned_stays: heen, planned_stays_return: terug });
      toast.success("Volgorde en nachten opgeslagen — je route wordt opnieuw berekend!");
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setHeen(norm(trip.planned_stays)); setTerug(norm(trip.planned_stays_return)); } }}>
      <DialogTrigger asChild>
        <button data-testid="route-stops-button"
          className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-[color] duration-200"
          title="Bekijk en wijzig de volgorde en het aantal nachten van al je tussenstops">
          <ListOrdered className="w-3.5 h-3.5" /> Volgorde &amp; nachten
        </button>
      </DialogTrigger>
      <DialogContent data-testid="route-stops-dialog" className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Volgorde &amp; nachten</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Dit is de volgorde waarin de route langs je overnachtingen wordt gepland. Verplaats, wijzig nachten of schrap — en herbereken daarna.
        </p>
        <div className="space-y-2">
          <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">Heenreis: {(trip.origin?.name || "vertrek").split(",")[0]} → {(trip.destination?.name || "bestemming").split(",")[0]}</p>
          {heen.length === 0 && <p className="text-xs text-muted-foreground bg-secondary/40 rounded-xl px-3 py-2.5">Geen geplande tussenstops — overnachtingen worden automatisch gekozen.</p>}
          {heen.map((s, i) => <StopRow key={`h-${i}-${s.location}`} s={s} idx={i} total={heen.length} prefix="heen" onMove={move(setHeen)} onNights={nights(setHeen)} onDelete={del(setHeen)} />)}
          <AddStopInline prefix="heen" onAdd={(loc) => setHeen((l) => [...l, { location: loc, nights: 1, name: null }])} />
        </div>
        {trip.round_trip && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">Terugreis</p>
            {terug.length === 0 && <p className="text-xs text-muted-foreground bg-secondary/40 rounded-xl px-3 py-2.5">Geen geplande tussenstops op de terugreis.</p>}
            {terug.map((s, i) => <StopRow key={`t-${i}-${s.location}`} s={s} idx={i} total={terug.length} prefix="terug" onMove={move(setTerug)} onNights={nights(setTerug)} onDelete={del(setTerug)} />)}
            <AddStopInline prefix="terug" onAdd={(loc) => setTerug((l) => [...l, { location: loc, nights: 1, name: null }])} />
          </div>
        )}
        <p className="text-xs text-muted-foreground">Totaal {totalNights} geplande nacht{totalNights !== 1 ? "en" : ""} op tussenstops — de einddatum schuift automatisch mee.</p>
        <Button data-testid="route-stops-save" onClick={save} disabled={busy}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Opslaan..." : "Opslaan & herbereken route"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
